// M80 (2026-04-26): NV reader 1 chương — flow Học theo vòng + AI on-demand + phao
//
// PRD: docs/PRD-M80.md
// Route: /noi-quy/module/:moduleId
//
// Flow:
//   1. Load chương + active set (nếu có) song song
//   2. Chưa có active set → hiện body chương + nút "Kiểm tra" → AI sinh
//   3. Có active set → ẩn body chương, hiện list câu + nút "Xem phao" inline
//   4. NV trả lời từng câu → AI chấm. Sai → giữ câu, retry. Đúng → câu kế tiếp.
//   5. Hết câu → nút "Hoàn thành bộ" → nút "Tạo bộ mới" (củng cố) hoặc "Sang chương khác"
//
// Cấu trúc component:
//   <RulesReaderM80Page>
//     <Header back + title + peek button + counter>
//     <BodyPanel collapsed?> + AnswerSection
//     <SetCompleteSummary> (sau khi xong)

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { renderRichText } from '../lib/renderRichText.js';

// ----------------------------------------------------------------------------
// Body chương — collapsible panel
// ----------------------------------------------------------------------------
function ChapterBody({ html, expanded }) {
  if (!html) return null;
  return (
    <div
      className={`bg-white border border-slate-200 rounded-2xl shadow-sm transition-all duration-300 overflow-hidden ${expanded ? 'max-h-[10000px] opacity-100' : 'max-h-0 opacity-0 border-0'}`}
    >
      <div className="p-6 rich-content select-none"
           onCopy={(e) => { e.preventDefault(); toast.error('Không được copy nội dung — phải tự nhớ và viết'); }}
           dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

// ----------------------------------------------------------------------------
// 1 câu hỏi (attempt) — text + textarea + feedback
// ----------------------------------------------------------------------------
// M287a (2026-05-25): badge phan biet cau lap (source_attempt_id != null) vs cau moi
function RepeatBadge({ isRepeat }) {
  if (!isRepeat) return null;
  return (
    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-semibold rounded-full border border-amber-300"
          title="Cau nay lap lai tu bo truoc vi ban da tra loi sai - phai lam dung 2 lan de khac cot.">
      🔁 Câu lặp
    </span>
  );
}

function AttemptCard({ attempt, position, onSubmit, isActive, isPassed, lastFeedback, moduleId }) {
  const isRepeat = !!attempt.source_attempt_id;
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const taRef = useRef(null);

  // Active câu thì auto-focus
  useEffect(() => {
    if (isActive && taRef.current) {
      setTimeout(() => taRef.current?.focus(), 50);
    }
  }, [isActive]);

  // M89 (2026-04-26): log cheat attempt — fire-and-forget, không block UX
  function logCheat(action, context = '') {
    api.rulesLogCheatAttempt({
      module_id: moduleId || null,
      question_id: attempt.id,
      action,
      context,
    }).catch(() => { /* swallow — best-effort */ });
  }

  async function handleSubmit() {
    const ans = answerText.trim();
    if (ans.length < 2) {
      toast.error('Câu trả lời quá ngắn — viết đầy đủ ý hơn');
      return;
    }
    setSubmitting(true);
    try {
      const r = await onSubmit(attempt.id, ans);
      if (r?.isCorrect) {
        // Đúng — clear textarea cho câu kế tiếp tự focus
        setAnswerText('');
      } else {
        // Sai — giữ text để NV sửa
        // Toast feedback đỏ ngắn (đã handle ở parent)
      }
    } finally { setSubmitting(false); }
  }

  function handleKeyDown(e) {
    // Ctrl+Enter để nộp nhanh
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
      return;
    }
    // M89 (2026-04-26): Chặn Ctrl+A/C/V/X/Insert + log audit
    if (e.ctrlKey || e.metaKey) {
      const k = String(e.key || '').toLowerCase();
      if (['a', 'c', 'v', 'x', 'insert'].includes(k)) {
        e.preventDefault();
        toast.error(`⚠️ Ctrl+${k.toUpperCase()} bị chặn — phải tự gõ. Sự kiện đã được ghi log.`);
        logCheat('keyboard_shortcut_in_answer', `Ctrl+${k.toUpperCase()}`);
      }
    }
  }

  function handlePaste(e) {
    e.preventDefault();
    toast.error('⚠️ Không được dán — phải tự gõ. Sự kiện đã được ghi log.');
    logCheat('paste_answer', '');
  }
  function handleCopy(e) {
    e.preventDefault();
    toast.error('⚠️ Không được copy. Sự kiện đã được ghi log.');
    logCheat('copy_in_answer', '');
  }
  function handleCut(e) {
    e.preventDefault();
    toast.error('⚠️ Không được cắt. Sự kiện đã được ghi log.');
    logCheat('cut_in_answer', '');
  }
  function handleContextMenu(e) {
    e.preventDefault();
    toast.error('⚠️ Menu chuột phải bị chặn. Sự kiện đã được ghi log.');
    logCheat('right_click_answer', '');
  }

  // Đã pass → readonly hiển thị tick xanh
  if (isPassed) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-emerald-700 font-semibold mb-1">Câu {position}<RepeatBadge isRepeat={isRepeat} /></div>
            <div className="text-slate-700 select-none"
                 onCopy={(e) => e.preventDefault()}>{attempt.question_text}</div>
          </div>
        </div>
      </div>
    );
  }

  // Chưa active (chưa tới lượt) → blur preview
  if (!isActive) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 opacity-50">
        <div className="text-xs text-slate-400 font-mono mb-1">Câu {position}<RepeatBadge isRepeat={isRepeat} /></div>
        <div className="text-slate-400 italic select-none"
             onCopy={(e) => e.preventDefault()}>(Đang chờ — hoàn thành câu trước đã)</div>
      </div>
    );
  }

  // Active — hiện textarea
  return (
    <div className="bg-white border-2 border-sky-400 rounded-xl p-4 shadow-md ring-1 ring-sky-200">
      <div className="text-xs text-sky-700 font-semibold mb-2">Câu {position}<RepeatBadge isRepeat={isRepeat} /> — đang trả lời</div>
      <div className="text-slate-800 font-medium mb-3 select-none"
           onCopy={(e) => e.preventDefault()}>{attempt.question_text}</div>

      {lastFeedback && (
        <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
          ❌ <strong>Chưa đúng.</strong> {lastFeedback}
          <div className="text-xs text-rose-500 mt-1 italic">Đọc kỹ lại tài liệu (xem phao nếu cần) rồi trả lời lại.</div>
        </div>
      )}

      <textarea
        ref={taRef}
        value={answerText}
        onChange={(e) => setAnswerText(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onCopy={handleCopy}
        onCut={handleCut}
        onContextMenu={handleContextMenu}
        placeholder="Viết câu trả lời của bạn... (KHÔNG được dán/copy/Ctrl+CVAX)"
        rows={4}
        className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none resize-y"
        disabled={submitting}
        autoComplete="off"
        autoCorrect="off"
        spellCheck="false"
      />
      <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
        <span className="text-xs text-slate-400">Ctrl+Enter để nộp nhanh</span>
        <button
          id="nv-active-submit-btn"
          onClick={handleSubmit}
          disabled={submitting || answerText.trim().length < 2}
          className="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {submitting ? '⏳ AI đang chấm...' : '✓ Nộp'}
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main page
// ----------------------------------------------------------------------------
export default function RulesReaderM80Page() {
  const { moduleId: moduleIdStr } = useParams();
  const moduleId = parseInt(moduleIdStr, 10);
  const nav = useNavigate();
  const user = useAuthStore(s => s.user);

  const [moduleData, setModuleData] = useState(null);     // {id, title, body_md, ...}
  const [activeSet, setActiveSet] = useState(null);        // {set, attempts, peekCount}
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [bodyExpanded, setBodyExpanded] = useState(false); // dùng cho khi có active set
  const [lastFeedbacks, setLastFeedbacks] = useState({});  // attemptId → feedback string
  // M83c: ref bodyExpanded để dùng trong useEffect (tránh stale closure khi đóng phao auto)
  const bodyExpandedRef = useRef(bodyExpanded);
  bodyExpandedRef.current = bodyExpanded;
  // M83d (2026-04-26): IntersectionObserver theo dõi nút "Nộp" của câu hiện tại
  const observerRef = useRef(null);
  const [completedJustNow, setCompletedJustNow] = useState(false);

  // ----- Initial load: module + active set ------------------------------------
  useEffect(() => {
    if (!Number.isInteger(moduleId) || moduleId <= 0) {
      toast.error('Module không hợp lệ');
      nav('/noi-quy', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [mod, activeRes] = await Promise.all([
          api.rulesModuleGet(moduleId),
          api.rulesM80SetActive(moduleId),
        ]);
        if (cancelled) return;
        setModuleData(mod);
        if (activeRes?.active) {
          setActiveSet(activeRes.active);
          setBodyExpanded(false);  // có active set → ẩn body
        } else {
          setBodyExpanded(true);   // chưa có set → hiện body để NV đọc
        }
      } catch (e) {
        if (!cancelled) {
          toast.error('Lỗi tải chương: ' + (e?.data?.error || e?.message));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [moduleId, nav]);

  // ----- Active attempt (câu chưa pass đầu tiên) -----------------------------
  const activeAttempt = activeSet?.attempts?.find(a => !a.is_correct) || null;
  const allPassed = activeSet?.attempts?.length > 0 && !activeAttempt;
  const totalAttempts = activeSet?.attempts?.length || 0;
  const passedCount = activeSet?.attempts?.filter(a => !!a.is_correct).length || 0;
  // M287a (2026-05-25): tach progress "Moi" (source_attempt_id NULL) vs "Lap" (cau sai bo truoc, lap 2 lan).
  const newAttempts = activeSet?.attempts?.filter(a => !a.source_attempt_id) || [];
  const repeatAttempts = activeSet?.attempts?.filter(a => !!a.source_attempt_id) || [];
  const newPassedCount = newAttempts.filter(a => !!a.is_correct).length;
  const repeatPassedCount = repeatAttempts.filter(a => !!a.is_correct).length;
  const peekCount = activeSet?.peekCount || 0;

  // ----- Auto-scroll khi active đổi ------------------------------------------
  useEffect(() => {
    if (activeAttempt?.id) {
      setTimeout(() => {
        const el = document.getElementById(`attempt-${activeAttempt.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [activeAttempt?.id]);

  // ----- Auto-scroll xuống banner "Hoàn thành" khi vừa complete --------------
  useEffect(() => {
    if (completedJustNow) {
      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }, 150);
    }
  }, [completedJustNow]);

  // ----- Auto-close phao khi nút "Nộp" của câu hiện tại vào viewport --------
  // M83d (2026-04-26): Thay logic "activeIndex >= 1" (không trigger nếu NV stuck
  // ở câu 1) bằng IntersectionObserver theo nút "✓ Nộp" (id="nv-active-submit-btn").
  // Nút Nộp luôn render dưới textarea câu đang trả lời. Khi NV scroll xuống
  // tới mức nút này vào viewport → NV đang focus trả lời → đóng phao để khỏi
  // che câu hỏi.
  useEffect(() => {
    if (!activeSet || !activeAttempt) return;
    // Đợi DOM render xong (button mount theo activeAttempt)
    const t = setTimeout(() => {
      const el = document.getElementById('nv-active-submit-btn');
      if (!el) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && bodyExpandedRef.current) {
            setBodyExpanded(false);
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(el);
      // Cleanup gắn vào ref để return cleanup outer
      observerRef.current = observer;
    }, 100);
    return () => {
      clearTimeout(t);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [activeSet?.set?.id, activeAttempt?.id]);

  // ----- Tạo bộ câu hỏi mới ---------------------------------------------------
  async function handleCreateSet() {
    if (creating) return;
    setCreating(true);
    setCompletedJustNow(false);
    try {
      const r = await api.rulesM80SetCreate(moduleId, false);
      // Reload state đầy đủ
      const full = await api.rulesM80SetGet(r.setId);
      setActiveSet(full);
      setBodyExpanded(false);  // ẩn body khi bắt đầu làm bài
      setLastFeedbacks({});
      toast.success(`✨ AI đã sinh ${r.attempts?.length || '?'} câu — bắt đầu làm bài!`);
    } catch (e) {
      toast.error('Lỗi tạo bộ: ' + (e?.data?.error || e?.message));
    } finally {
      setCreating(false);
    }
  }

  // ----- Toggle xem phao ------------------------------------------------------
  async function handleTogglePeek() {
    // Nếu đang ẩn → mở (sẽ log) ; nếu đang mở → đóng (KHÔNG log)
    if (!bodyExpanded) {
      try {
        const r = await api.rulesM80SetPeek(activeSet.set.id, activeAttempt?.id || null);
        setActiveSet(prev => prev ? { ...prev, peekCount: r.peekCount } : prev);
      } catch (e) {
        toast.error('Lỗi log phao: ' + (e?.data?.error || e?.message));
        return;
      }
    }
    setBodyExpanded(v => !v);
  }

  // ----- Nộp câu trả lời ------------------------------------------------------
  async function handleSubmitAnswer(attemptId, answerText) {
    try {
      const r = await api.rulesM80AnswerSubmit(attemptId, answerText);
      if (r.isCorrect) {
        toast.success('✅ Đúng rồi!', { duration: 2000 });
        // Update attempt locally
        setActiveSet(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            attempts: prev.attempts.map(a => a.id === attemptId ? { ...a, is_correct: true, passed_at: new Date().toISOString() } : a),
            peekCount: r.peekCount,
          };
        });
        setLastFeedbacks(prev => ({ ...prev, [attemptId]: null }));
      } else {
        // Sai — lưu feedback hiển thị inline
        setLastFeedbacks(prev => ({ ...prev, [attemptId]: r.feedback || 'Chưa đúng — đọc lại tài liệu.' }));
        setActiveSet(prev => prev ? { ...prev, peekCount: r.peekCount } : prev);
      }
      return r;
    } catch (e) {
      toast.error('Lỗi chấm: ' + (e?.data?.error || e?.message));
      throw e;
    }
  }

  // ----- Hoàn thành bộ --------------------------------------------------------
  async function handleCompleteSet() {
    if (completing || !activeSet) return;
    setCompleting(true);
    try {
      await api.rulesM80SetComplete(activeSet.set.id);
      toast.success('🎉 Hoàn thành bộ!');
      setCompletedJustNow(true);
      setActiveSet(null);
      setBodyExpanded(true);  // mở body lại
    } catch (e) {
      toast.error('Lỗi hoàn thành: ' + (e?.data?.error || e?.message));
    } finally {
      setCompleting(false);
    }
  }

  // ============================================================================
  // RENDER
  // ============================================================================
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Đang tải...</div>;
  }
  if (!moduleData) {
    return <div className="min-h-screen flex items-center justify-center text-rose-500">Không tìm thấy chương.</div>;
  }

  const bodyHtml = renderRichText(moduleData.body_md || '');

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header sticky */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
          <Link to="/noi-quy" className="text-sm text-slate-500 hover:text-slate-800">← Danh sách chương</Link>
          <div className="text-sm text-slate-300">|</div>
          <h1 className="text-base font-semibold text-slate-800 flex-1 truncate">{moduleData.title}</h1>
          {activeSet && (
            <button
              onClick={handleTogglePeek}
              className={`text-sm font-semibold px-3 py-1.5 rounded-lg border transition ${
                bodyExpanded
                  ? 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200'
                  : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
              title={bodyExpanded ? 'Đóng phao — quay lại làm bài' : 'Xem lại nội dung chương (sẽ ghi log)'}
            >
              {bodyExpanded ? '📖 Đóng phao' : '👀 Xem phao'}
              {peekCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-amber-200 text-amber-900 text-xs rounded-full font-bold">
                  {peekCount}
                </span>
              )}
            </button>
          )}
        </div>
        {/* Progress bar nếu có active set */}
        {activeSet && totalAttempts > 0 && (
          <div className="max-w-3xl mx-auto px-4 pb-2">
            <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
              <span>Tiến độ:</span>
              <span className="font-bold text-emerald-600">{passedCount}/{totalAttempts}</span>
              {repeatAttempts.length > 0 && (
                <span className="text-[11px] text-slate-400">
                  (✨ Mới <strong className="text-sky-700">{newPassedCount}/{newAttempts.length}</strong>
                  {' '}· 🔁 Lặp <strong className="text-amber-700">{repeatPassedCount}/{repeatAttempts.length}</strong>)
                </span>
              )}
              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden min-w-[80px]">
                <div
                  className="h-full bg-gradient-to-r from-sky-400 to-emerald-500 transition-all duration-500"
                  style={{ width: `${(passedCount / totalAttempts) * 100}%` }}
                />
              </div>
            </div>
            {repeatAttempts.length > 0 && (
              <div className="text-[10px] text-slate-400 mt-1 italic">
                💡 Câu lặp = câu bạn từng trả lời sai, lặp 2 lần để khắc cốt.
              </div>
            )}
          </div>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Body chương — collapsible khi có active set */}
        <ChapterBody html={bodyHtml} expanded={bodyExpanded} />

        {/* Khi chưa có active set → CTA Kiểm tra */}
        {!activeSet && !completedJustNow && (
          <div className="bg-white border-2 border-dashed border-sky-300 rounded-2xl p-8 text-center">
            <div className="text-4xl mb-3">🧠</div>
            <div className="text-lg font-bold text-slate-800 mb-2">Sẵn sàng kiểm tra?</div>
            <div className="text-sm text-slate-600 mb-5">
              AI sẽ sinh bộ câu hỏi dựa trên nội dung chương vừa đọc.<br />
              Bạn cần trả lời <strong>đúng tất cả</strong> mới hoàn thành bộ.
            </div>
            <button
              onClick={handleCreateSet}
              disabled={creating}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 text-white font-semibold rounded-xl shadow-md disabled:opacity-50 transition"
            >
              {creating ? (
                <><span className="animate-spin">⏳</span> AI đang sinh câu hỏi...</>
              ) : (
                <>🚀 Bắt đầu kiểm tra</>
              )}
            </button>
          </div>
        )}

        {/* Khi có active set → list attempts */}
        {activeSet && (
          <>
            {!bodyExpanded && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                💡 Nội dung chương đang ẩn để bạn tự nhớ. Cần xem lại? Bấm <strong>"Xem phao"</strong> trên đầu trang
                {peekCount > 0 && <span> (đã xem <strong>{peekCount}</strong> lần)</span>}.
              </div>
            )}

            <div className="space-y-3">
              {activeSet.attempts.map((a, i) => (
                <div key={a.id} id={`attempt-${a.id}`}>
                  <AttemptCard
                    attempt={a}
                    position={i + 1}
                    onSubmit={handleSubmitAnswer}
                    isActive={activeAttempt?.id === a.id}
                    isPassed={!!a.is_correct}
                    lastFeedback={lastFeedbacks[a.id] || null}
                    moduleId={moduleId}
                  />
                </div>
              ))}
            </div>

            {/* Khi đã pass hết → CTA Hoàn thành */}
            {allPassed && (
              <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-6 text-center">
                <div className="text-3xl mb-2">🎯</div>
                <div className="text-lg font-bold text-emerald-800 mb-1">Đã trả lời đúng hết!</div>
                <div className="text-sm text-slate-600 mb-4">
                  Bấm "Hoàn thành" để khoá bộ này lại. Sau đó có thể tạo bộ mới (củng cố) hoặc sang chương khác.
                </div>
                <button
                  onClick={handleCompleteSet}
                  disabled={completing}
                  className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md disabled:opacity-50"
                >
                  {completing ? '⏳ Đang lưu...' : '✅ Hoàn thành bộ'}
                </button>
              </div>
            )}
          </>
        )}

        {/* Banner "vừa hoàn thành" — đặt CUỐI trang để NV vừa scroll xuống nút
            "Hoàn thành bộ" sẽ thấy tiếp banner cùng tầm nhìn (không bị nhảy
            lên đầu). Body chương ở trên giữ nguyên để NV tham khảo lại nếu cần. */}
        {completedJustNow && (
          <div className="bg-gradient-to-r from-emerald-50 to-sky-50 border-2 border-emerald-300 rounded-2xl p-5 text-center">
            <div className="text-3xl mb-2">🎉</div>
            <div className="text-lg font-bold text-emerald-800 mb-1">Hoàn thành bộ câu hỏi!</div>
            <div className="text-sm text-slate-600 mb-4">Bạn đã trả lời đúng tất cả câu trong bộ này.</div>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={handleCreateSet}
                disabled={creating}
                className="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-lg disabled:opacity-50"
              >
                {creating ? '⏳ Đang sinh...' : '🔄 Tạo bộ mới (củng cố)'}
              </button>
              <Link to="/noi-quy" className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg">
                ➡ Sang chương khác
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
